<template>
  <v-expansion-panel
    data-component-id="Competence"
    :readonly="editingIndex !== -1 || isAddMode"
    style="border: 1px solid #d7d7d7; border-radius: 10px; background-color: transparent;"
  >
    <v-expansion-panel-header
      class="flex-row-reverse"
      style="gap: 16px;"
      color="grey lighten-2"
    >
      <div
        class="
          d-flex justify-end
          text-right
        "
        style="gap: 16px;"
      >
        <v-btn
          v-if="editingIndex <= -1 && !isAddMode"
          icon
          :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
          @click.stop="setAddMode(true)"
        >
          <v-icon class="justify-end">
            mdi-plus
          </v-icon>
        </v-btn>
        <v-btn
          v-if="editingIndex > -1 || isAddMode"
          :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
          @click.stop="handleCancelAction"
        >
          <span class="d-none d-md-block">Annuler les modifications</span>
          <v-icon class="d-block d-md-none">
            mdi-refresh
          </v-icon>
        </v-btn>
        <v-btn
          v-if="editingIndex > -1 || isAddMode"
          color="primary"
          :disabled="(permission ? !canPerformAction(`EDIT_${permission}`) : false) || !isValidForm"
          @click.stop="saveChanges()"
        >
          <span class="d-none d-md-block">Enregistrer</span>
          <v-icon class="d-block d-md-none">
            mdi-content-save
          </v-icon>
        </v-btn>
      </div>
      <h3>{{ title }}</h3>
    </v-expansion-panel-header>

    <v-expansion-panel-content
      class="pa-5"
      color="transparent"
      style="border-bottom-right-radius: 10px; border-bottom-left-radius: 10px;"
    >
      <v-form
        v-model="isValidForm"
        class="d-flex flex-column"
        style="gap: 16px;"
      >
        <v-list
          v-if="isAddMode"
          dense
          class="
            relative d-flex flex-wrap
            px-5
          "
          style="min-height: 90px; border-radius: 10px; border: 2px dashed red;"
        >
          <v-row>
            <template v-for="(field, fieldIndex) in copyFields">
              <v-col
                v-if="!field.hidden"
                :key="fieldIndex"
                :cols="12"
                :md="field.fullWidth ? 12 : 6"
              >
                <v-list-item
                  v-if="!field.hidden"
                  :key="fieldIndex"
                  class="px-0 px-md-4"
                >
                  <v-list-item-icon>
                    <v-icon color="primary">
                      mdi-{{ field.icon }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content
                    class="
                      align-self-baseline
                      overflow-visible
                    "
                  >
                    <v-skeleton-loader
                      :loading="isLoading"
                      type="list-item"
                    >
                      <v-list-item-title
                        class="overflow-visible"
                      >
                        <slot
                          :name="field.name"
                          :field="field"
                          :value="data[field.name]"
                        >
                          <component
                            :is="field.component ?? 'GenericField'"
                            :id="field.name"
                            :key="field.key || field.name"
                            v-model="data[field.name]"
                            :name="field.name"
                            :items="getFieldReferences(field.optionsByReference)"
                            :input="field"
                            :data="data"
                            :prepend-icon="''"
                            :field="field"
                            :classes="getInputClasses(field)"
                          />
                        </slot>
                      </v-list-item-title>
                    </v-skeleton-loader>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </template>
          </v-row>
        </v-list>

        <template
          v-if="copyItems?.length"
        >
          <section
            v-for="(item, itemIndex) in copyItems"
            :key="itemIndex"
            class="d-flex"
            style="gap: 32px;"
          >
            <v-list
              dense
              class="
                relative d-flex flex-wrap flex-grow-1
                px-5
              "
              style="min-height: 90px; border-radius: 10px;"
              :style="editingIndex === itemIndex ? 'border: 2px dashed red;' : ''"
            >
              <v-row>
                <template
                  v-for="(field, fieldIndex) in fields"
                >
                  <v-col
                    :key="fieldIndex"
                    :cols="12"
                    :md="field.fullWidth ? 12 : 6"
                  >
                    <v-list-item
                      v-if="!field.hiddem"
                      :key="fieldIndex"
                      class="px-0 px-md-4 break-word"
                    >
                      <v-list-item-icon>
                        <v-icon :color="getFormattedValue(field, item) === 'N/A' ? 'grey' : 'primary'">
                          mdi-{{ field.icon }}
                        </v-icon>
                      </v-list-item-icon>

                      <v-list-item-content
                        class="
                          align-self-baseline
                          overflow-visible
                        "
                      >
                        <v-skeleton-loader
                          :loading="isLoading"
                          type="list-item"
                        >
                          <v-list-item-title
                            class="overflow-visible"
                          >
                            <slot
                              v-if="editingIndex === itemIndex"
                              :name="field.name"
                              :field="field"
                              :value="data[field.name]"
                            >
                              <component
                                :is="field.component ?? 'GenericField'"
                                :id="field.name"
                                v-model="data[field.name]"
                                :name="field.name"
                                :items="getFieldReferences(field.optionsByReference)"
                                :data="data"
                                :input="field"
                                :prepend-icon="''"
                                :field="field"
                                :classes="getInputClasses(field)"
                              />
                            </slot>

                            <span
                              v-else-if="getFormattedValue(field, item) !== 'N/A'"
                              class="body-1 font-family-saira font-weight-regular"
                            >
                              {{ getFormattedValue(field, item) }}
                            </span>
                            <span
                              v-else-if="getFormattedValue(field, item) === 'N/A'"
                              class="body-1 font-family-saira font-weight-regular text--secondary"
                            >
                              {{ getFormattedValue(field, item) }}
                            </span>
                          </v-list-item-title>
                        </v-skeleton-loader>
                        <v-list-item-subtitle
                          v-if="!editingIndex === -1 && !isAddMode"
                        >
                          {{ field.label }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-col>
                </template>
              </v-row>
            </v-list>

            <div
              v-if="editingIndex !== itemIndex"
              class="d-flex flex-column"
            >
              <v-btn
                icon
                :disabled="(permission ? !canPerformAction(`EDIT_${permission}`) : false) || isAddMode"
                @click="startEditing(true, itemIndex, item)"
              >
                <v-icon>
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                :disabled="(permission ? !canPerformAction(`EDIT_${permission}`) : false) || isAddMode"
                @click="handleDelete(itemIndex)"
              >
                <v-icon>
                  mdi-delete
                </v-icon>
              </v-btn>
            </div>
          </section>
        </template>
        <div
          v-else
          class="text-center"
        >
          Aucune information renseignée
        </div>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import moment from 'moment';

import GenericField from '@/components/dynamic/form/templates/GenericField.vue';
import DatePicker from '@/components/dynamic/form/components/DatePicker.vue';
import PositionInput from '@/components/forms/PositionInput.vue';

export default {
  name: 'Competence',
  components: { GenericField, DatePicker, PositionInput },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      data: {},
      copyItems: JSON.parse(JSON.stringify(this.items)) || [],
      copyFields: JSON.parse(JSON.stringify(this.fields)),
      isAddMode: false,
      isValidForm: false,

      editingIndex: -1
    };
  },
  computed: {
    ...mapState(['references', 'focusMode']),
    ...mapGetters(['canPerformAction']),
    collaboratorName () {
      return collaborator => `${collaborator.firstname} ${collaborator.lastname}`;
    },
    formatedDate () {
      return date => {
        if (!date) return '';

        return moment(date).format('DD-MM-YYYY');
      };
    }
  },
  watch: {
    items (value) {
      this.copyItems = JSON.parse(JSON.stringify(value));
    }
  },
  methods: {
    setAddMode (value) {
      this.isAddMode = value;

      this.data = this.initializeData({});

      if (value) {
        this.$emit('is-edit-mode', value);
      }
    },
    startEditing (value, index, item) {
      this.editingIndex = index;
      this.data = this.initializeData(item);

      if (value) {
        this.$emit('is-edit-mode', value);
      }
    },
    handleDelete (index) {
      this.saveChanges(index, true);
    },
    saveChanges (index, isDelete) {
      const indexAux = this.editingIndex !== -1 ? this.editingIndex : index;
      this.$emit('save-changes', indexAux !== -1 ? indexAux : null, this.data, isDelete);

      this.editingIndex = -1;
      this.isAddMode = false;
    },
    handleCancelAction (item) {
      this.editingIndex = -1;
      this.isAddMode = false;
      this.initializeData(item);
    },
    getFormattedValue (field, item) {
      const value = (item && field.computed)
        ? this.getNestedValue(item, field.computed)
        : item[field.name];

      return this.formatFieldValue(field, value, item);
    },
    getNestedValue (obj, path) {
      return path
        .split('.')
        .reduce((acc, part) => (acc && acc[part]), obj);
    },
    initializeData (item) {
      const competence = item;

      this.fields.forEach(field => {
        this.$set(
          this.data,
          field.name,
          field.computed ? this.getNestedValue(competence, field.computed) || null : competence[field.name] || null
        );
      });

      return this.data;
    },
    getFieldReferences (optionsByReference) {
      if (!optionsByReference) return [];

      return Object.entries(this.references[optionsByReference]).map(([key, value]) => ({ text: value, value: key }));
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    },
    formatFieldValue (field, value, item) {
      if ([null, undefined, ''].includes(value)) {
        return 'N/A';
      }

      if (field.viewType === 'Switch') {
        return value ? 'Oui' : 'Non';
      }

      if (field.component === 'DatePicker') {
        return this.formatedDate(value);
      }

      // Handle Reference type
      if (field.optionsByReference) {
        return this.references[field.optionsByReference][value];
      }

      // Handle Computed Label
      if (field.computedLabel) {
        return this.getNestedValue(item, field.computedLabel);
      }

      return value;
    }
  }

};
</script>
